import { ResultBadge } from "@/components/badge/result-badge";
import { RowCard } from "@/components/list/row-card";
import { Chip, type ChipTone } from "@/components/badge/chip";
import { prdDetailPath, versionDetailPath } from "@/lib/paths";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { versionLabel } from "@/lib/version";
import { useApps } from "@/hooks/apps";
import { StateButton as VersionStateButton } from "@/screens/Versions/columns";
import { SummaryCell } from "./summary-cell";
import type { Report } from "@/types/domain";
import { DATE_FULL } from "@/lib/date";

const ownerTypeTone: Record<string, ChipTone> = {
  version: "blue",
  prd: "purple",
};

// Report owner (version / prd) line.
const OwnerLine = ({ report }: { report: Report }) => {
  const apps = useApps();
  if (report.ownerType === "version") {
    const v = report.version;
    if (v) {
      return (
        <Link
          to={versionDetailPath(v)}
          className="inline-flex min-w-0 items-center gap-1.5 hover:text-primary"
        >
          <span className="truncate">
            {versionLabel(v, apps) || "Unknown version"}
          </span>
          {v.state !== undefined && <VersionStateButton stateCode={v.state} />}
        </Link>
      );
    }
    return (
      <Link to={`/versions/${report.ownerId}`} className="hover:text-primary">
        Version: {report.ownerId}
      </Link>
    );
  }
  if (report.ownerType === "prd") {
    const p = report.prd;
    if (p) {
      return (
        <Link to={prdDetailPath(p.id)} className="min-w-0 truncate hover:text-primary">
          {p.docName || "Unknown PRD"}
          {p.owner && <span className="ml-1 text-muted-foreground">· {p.owner.name}</span>}
        </Link>
      );
    }
    return (
      <Link to={prdDetailPath(report.ownerId)} className="hover:text-primary">
        PRD: {report.ownerId}
      </Link>
    );
  }
  return <span>Unknown</span>;
};

export const ReportCard = ({ report }: { report: Report }) => {
  const name = report.name || "Untitled report";
  return (
    <RowCard className="flex-col items-stretch gap-0">
      <div className="flex items-center gap-3">
        {report.url ? (
          <a
            href={report.url}
            target="_blank"
            rel="noreferrer"
            title={report.url}
            className="min-w-0 flex-1 truncate font-medium hover:text-primary hover:underline"
          >
            {name}
          </a>
        ) : (
          <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
        )}
        <Chip tone={ownerTypeTone[report.ownerType]}>
          {report.ownerType === "version" ? "Version" : "PRD"}
        </Chip>
        <ResultBadge result={report.result} className="shrink-0" />
        <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {report.createdAt ? format(new Date(report.createdAt), DATE_FULL) : ""}
        </span>
      </div>

      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        <OwnerLine report={report} />
      </div>

      {report.summary && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          <SummaryCell report={report} summary={report.summary} />
        </div>
      )}
    </RowCard>
  );
};

export default ReportCard;
